import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Filter, Users, Award, Search, Check, X, ChevronDown, ChevronUp, Trash2, UserPlus, Edit, Save
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AreaFiltro = () => {
  const [filteredJudgments, setFilteredJudgments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [athletes, setAthletes] = useState([]);
  const [assignModal, setAssignModal] = useState(null); // {filteredId, athleteName, athleteSurname}
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  const [athleteSearchTerm, setAthleteSearchTerm] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [editNameModal, setEditNameModal] = useState(null); // {filteredId, name, surname}
  const [editScoresModal, setEditScoresModal] = useState(null); // {filteredId, judgmentIndex, tecnica, coreografia, immagine}

  useEffect(() => {
    fetchFilteredJudgments();
    fetchAthletes();
  }, []);

  const fetchFilteredJudgments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/area-filtro`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFilteredJudgments(response.data);
    } catch (error) {
      console.error('Error fetching filtered judgments:', error);
      toast.error('Errore nel caricamento dell\'Area FILTRO');
    } finally {
      setLoading(false);
    }
  };

  const fetchAthletes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter only athletes
      const athleteUsers = response.data.filter(u => 
        (u.roles && u.roles.includes('atleta')) || u.role === 'atleta'
      );
      setAthletes(athleteUsers);
    } catch (error) {
      console.error('Error fetching athletes:', error);
    }
  };

  const toggleItem = (id) => {
    setExpandedItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleAssign = async () => {
    if (!selectedAthleteId || !assignModal) return;

    setAssigning(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/area-filtro/${assignModal.filteredId}/assign?athlete_id=${selectedAthleteId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setAssignModal(null);
      setSelectedAthleteId('');
      setAthleteSearchTerm('');
      fetchFilteredJudgments();
    } catch (error) {
      console.error('Error assigning judgment:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'assegnazione');
    } finally {
      setAssigning(false);
    }
  };

  const handleDelete = async (filteredId, athleteName) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il giudizio di ${athleteName} dall'Area FILTRO?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/area-filtro/${filteredId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Giudizio eliminato');
      fetchFilteredJudgments();
    } catch (error) {
      console.error('Error deleting judgment:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUpdateName = async () => {
    if (!editNameModal) return;
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/area-filtro/${editNameModal.filteredId}/athlete-name?name=${encodeURIComponent(editNameModal.name)}&surname=${encodeURIComponent(editNameModal.surname)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Nome atleta aggiornato');
      setEditNameModal(null);
      fetchFilteredJudgments();
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleUpdateScores = async () => {
    if (!editScoresModal) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API}/admin/area-filtro/${editScoresModal.filteredId}/scores?judgment_index=${editScoresModal.judgmentIndex}&tecnica=${editScoresModal.tecnica}&coreografia=${editScoresModal.coreografia}&immagine=${editScoresModal.immagine}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Voti aggiornati - Nuova media: ${response.data.new_average}/30 (${response.data.new_credits} crediti)`);
      setEditScoresModal(null);
      fetchFilteredJudgments();
    } catch (error) {
      console.error('Error updating scores:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const filteredResults = filteredJudgments.filter(j => 
    j.athlete_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    j.athlete_surname.toLowerCase().includes(searchTerm.toLowerCase()) ||
    j.event_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    j.competition_number.includes(searchTerm)
  );

  const filteredAthletes = athletes.filter(a =>
    a.name.toLowerCase().includes(athleteSearchTerm.toLowerCase()) ||
    a.surname.toLowerCase().includes(athleteSearchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(athleteSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100">
          <CardTitle className="flex items-center">
            <Filter className="w-6 h-6 mr-2 text-orange-600" />
            Area FILTRO
            {filteredJudgments.length > 0 && (
              <Badge className="ml-3 bg-orange-500">{filteredJudgments.length}</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Giudizi di atleti non registrati al momento della pubblicazione. 
            Assegnali agli atleti quando si registrano alla piattaforma.
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Cerca per nome, cognome, evento o N° gara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="area-filtro-search"
              />
            </div>
          </div>

          {filteredResults.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">
                {filteredJudgments.length === 0 
                  ? "Nessun giudizio in Area FILTRO" 
                  : "Nessun risultato per la ricerca"}
              </p>
              <p className="text-sm mt-1">
                I giudizi degli atleti non registrati appariranno qui
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredResults.map((judgment) => (
                <Card key={judgment.id} className="overflow-hidden border-orange-200">
                  <div
                    onClick={() => toggleItem(judgment.id)}
                    className="p-4 bg-orange-50 cursor-pointer flex items-center justify-between hover:bg-orange-100"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-1">
                        <Badge variant="outline" className="font-mono bg-white">
                          N° {judgment.competition_number}
                        </Badge>
                        <span className="font-semibold text-lg">
                          {judgment.athlete_name} {judgment.athlete_surname}
                        </span>
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        {judgment.event_code && (
                          <Badge variant="secondary" className="text-xs">{judgment.event_code}</Badge>
                        )}
                        <span>{judgment.event_title}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className="text-lg font-bold text-orange-600">
                          Media: {Math.round(judgment.average_total)}/30
                        </div>
                        <Badge className="bg-purple-500">
                          +{judgment.technical_credits_calculated} Crediti
                        </Badge>
                      </div>
                      {expandedItems[judgment.id] ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>
                  
                  {expandedItems[judgment.id] && (
                    <CardContent className="pt-4 border-t border-orange-200">
                      {/* Judgments Table */}
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-2">Giudizi Ricevuti</h4>
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2 text-left">Giudice</th>
                              <th className="p-2 text-center">Tecnica</th>
                              <th className="p-2 text-center">Coreografia</th>
                              <th className="p-2 text-center">Immagine</th>
                              <th className="p-2 text-center">Totale</th>
                              <th className="p-2 text-center">Azioni</th>
                            </tr>
                          </thead>
                          <tbody>
                            {judgment.judgments.map((j, idx) => (
                              <tr key={idx} className="border-t">
                                <td className="p-2">{j.judge_name}</td>
                                <td className="p-2 text-center">{j.tecnica}</td>
                                <td className="p-2 text-center">{j.coreografia}</td>
                                <td className="p-2 text-center">{j.immagine}</td>
                                <td className="p-2 text-center font-bold">{j.total}</td>
                                <td className="p-2 text-center">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditScoresModal({
                                        filteredId: judgment.id,
                                        judgmentIndex: idx,
                                        judgeName: j.judge_name,
                                        tecnica: j.tecnica,
                                        coreografia: j.coreografia,
                                        immagine: j.immagine
                                      });
                                    }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-orange-50">
                            <tr>
                              <td className="p-2 font-bold">MEDIA</td>
                              <td colSpan={4}></td>
                              <td className="p-2 text-center font-bold text-lg text-orange-600">
                                {Math.round(judgment.average_total)}/30
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      
                      {/* Info */}
                      <div className="text-sm text-gray-500 mb-4">
                        <p>Pubblicato il: {new Date(judgment.published_at).toLocaleDateString('it-IT', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</p>
                        <p>Da: {judgment.published_by_name}</p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditNameModal({
                              filteredId: judgment.id,
                              name: judgment.athlete_name,
                              surname: judgment.athlete_surname
                            });
                          }}
                          variant="outline"
                          className="flex-1"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Modifica Nome
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAssignModal({
                              filteredId: judgment.id,
                              athleteName: judgment.athlete_name,
                              athleteSurname: judgment.athlete_surname
                            });
                          }}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          data-testid={`assign-btn-${judgment.id}`}
                        >
                          <UserPlus className="w-4 h-4 mr-2" />
                          Assegna ad Atleta
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(judgment.id, `${judgment.athlete_name} ${judgment.athlete_surname}`);
                          }}
                          variant="destructive"
                          data-testid={`delete-btn-${judgment.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full p-6">
            <h3 className="text-xl font-bold mb-4">
              Assegna Giudizio
            </h3>
            <p className="text-gray-600 mb-4">
              Seleziona l&apos;atleta a cui assegnare il giudizio di{' '}
              <strong>{assignModal.athleteName} {assignModal.athleteSurname}</strong>
            </p>
            
            {/* Athlete Search */}
            <div className="mb-4">
              <Label htmlFor="athlete-search">Cerca Atleta</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="athlete-search"
                  placeholder="Nome, cognome o email..."
                  value={athleteSearchTerm}
                  onChange={(e) => setAthleteSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="athlete-search-input"
                />
              </div>
            </div>
            
            {/* Athletes List */}
            <div className="max-h-64 overflow-y-auto border rounded-lg mb-4">
              {filteredAthletes.length === 0 ? (
                <p className="p-4 text-center text-gray-500">Nessun atleta trovato</p>
              ) : (
                filteredAthletes.map((athlete) => (
                  <div
                    key={athlete.id}
                    onClick={() => setSelectedAthleteId(athlete.id)}
                    className={`p-3 border-b cursor-pointer flex items-center justify-between transition-colors ${
                      selectedAthleteId === athlete.id
                        ? 'bg-green-100 border-l-4 border-l-green-500'
                        : 'hover:bg-gray-50'
                    }`}
                    data-testid={`athlete-option-${athlete.id}`}
                  >
                    <div>
                      <p className="font-medium">{athlete.name} {athlete.surname}</p>
                      <p className="text-sm text-gray-500">{athlete.email}</p>
                    </div>
                    {selectedAthleteId === athlete.id && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                ))
              )}
            </div>
            
            {/* Actions */}
            <div className="flex space-x-3">
              <Button
                variant="outline"
                onClick={() => {
                  setAssignModal(null);
                  setSelectedAthleteId('');
                  setAthleteSearchTerm('');
                }}
                className="flex-1"
                disabled={assigning}
              >
                Annulla
              </Button>
              <Button
                onClick={handleAssign}
                disabled={!selectedAthleteId || assigning}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="confirm-assign-btn"
              >
                {assigning ? 'Assegnazione...' : 'Conferma Assegnazione'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Name Modal */}
      {editNameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Modifica Nome Atleta</h3>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Nome</Label>
                <Input
                  id="edit-name"
                  value={editNameModal.name}
                  onChange={(e) => setEditNameModal({...editNameModal, name: e.target.value})}
                  placeholder="Nome"
                />
              </div>
              <div>
                <Label htmlFor="edit-surname">Cognome</Label>
                <Input
                  id="edit-surname"
                  value={editNameModal.surname}
                  onChange={(e) => setEditNameModal({...editNameModal, surname: e.target.value})}
                  placeholder="Cognome"
                />
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditNameModal(null)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleUpdateName}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Scores Modal */}
      {editScoresModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-xl font-bold mb-4">Modifica Voti</h3>
            <p className="text-gray-600 mb-4">
              Giudice: <strong>{editScoresModal.judgeName}</strong>
            </p>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-tecnica">Tecnica (1-10)</Label>
                <Input
                  id="edit-tecnica"
                  type="number"
                  min="1"
                  max="10"
                  value={editScoresModal.tecnica}
                  onChange={(e) => setEditScoresModal({...editScoresModal, tecnica: parseInt(e.target.value) || 1})}
                />
              </div>
              <div>
                <Label htmlFor="edit-coreografia">Coreografia (1-10)</Label>
                <Input
                  id="edit-coreografia"
                  type="number"
                  min="1"
                  max="10"
                  value={editScoresModal.coreografia}
                  onChange={(e) => setEditScoresModal({...editScoresModal, coreografia: parseInt(e.target.value) || 1})}
                />
              </div>
              <div>
                <Label htmlFor="edit-immagine">Immagine (1-10)</Label>
                <Input
                  id="edit-immagine"
                  type="number"
                  min="1"
                  max="10"
                  value={editScoresModal.immagine}
                  onChange={(e) => setEditScoresModal({...editScoresModal, immagine: parseInt(e.target.value) || 1})}
                />
              </div>
              <div className="bg-gray-100 p-3 rounded-lg text-center">
                <p className="text-sm text-gray-500">Nuovo Totale</p>
                <p className="text-2xl font-bold">
                  {(editScoresModal.tecnica || 0) + (editScoresModal.coreografia || 0) + (editScoresModal.immagine || 0)}/30
                </p>
              </div>
            </div>
            
            <div className="flex space-x-4 mt-6">
              <Button
                variant="outline"
                onClick={() => setEditScoresModal(null)}
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={handleUpdateScores}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                Salva Voti
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaFiltro;
